/**
 * HOME BLOCK 03 - 3 Image Grid
 * GSAP ScrollTrigger Animation
 *
 * FEATURES:
 * - Sticky scroll effect using GSAP pin
 * - Desktop only (≥1024px) via ScrollTrigger.matchMedia
 * - Multiple section instances supported
 * - Button fade animation on scroll
 * - Drawer controls
 * - Singleton pattern to prevent multiple initializations
 *
 * * TODO:
 * 1. Pin distance is calculated once and never refreshed on image load.
 *    stickyHeight/smallHeight are captured at init — if lazy images in the
 *    small column load later and change height, the end value is stale.
 *    Fix: use a dynamic end function like end: () => '+=' + calculatePinDistance()
 *
 * 2. No invalidateOnRefresh or image load listener.
 *    If lazy images change layout after init, the pin end-point will be wrong.
 *    Fix: add invalidateOnRefresh: true to the pin ScrollTrigger and listen
 *    for image load events to call ScrollTrigger.refresh()
 *
 * 3. Sub-drawer close has a hardcoded 400ms setTimeout before closing parent drawer.
 *    If the CSS transition duration changes, these fall out of sync.
 *    Fix: use transitionend event instead of setTimeout
 *
 * 4. var sectionId is redeclared twice in the Escape key handler (lines ~312, ~331).
 *    Works due to var hoisting but is a code smell. Fix: rename or use let.
 */

// Use global namespace to prevent duplicate initializations
window.HomeBlock03GSAP = window.HomeBlock03GSAP || (function() {
  'use strict';

  // =============================================
  // CONFIGURATION
  // =============================================

  const CONFIG = {
    BREAKPOINT: 1024,
    DEBUG: false, // Set to true for ScrollTrigger markers

    SELECTORS: {
      section: '[data-section-id]',
      grid: '.animation-grid-container',
      largeColumn: '.animation-large-column',
      stickyContent: '.animation-sticky-content',
      smallColumn: '.animation-small-column',
      smallColumnInner: '.callout-column-small-inner',
      triggerButton: '.animation-trigger-button',
      largeButton: '.animation-large-button',

      // Drawer selectors
      drawerTrigger: '[data-drawer-trigger]',
      drawerOne: '[data-drawer-one]',
      drawerOverlay: '[data-drawer-overlay]',
      drawerClose: '[data-drawer-close]',
      subDrawerTrigger: '[data-sub-drawer-trigger]',
      subDrawerClose: '[data-sub-drawer-close]',
      subDrawerOverlay: '[data-sub-drawer-overlay]',
      drawerSub: '[data-drawer-sub]'
    }
  };

  // Track our own ScrollTrigger instances so we only kill ours
  var ownScrollTriggers = [];

  // Track whether drawer listeners have been bound
  var drawerListenersBound = false;

  // =============================================
  // DEBUG LOGGER
  // =============================================

  function log(message, data) {
    if (CONFIG.DEBUG) {
      if (data !== undefined) {
        console.log('[HomeBlock03-GSAP]', message, data);
      } else {
        console.log('[HomeBlock03-GSAP]', message);
      }
    }
  }

  // =============================================
  // SCROLL TRIGGER HELPERS
  // =============================================

  function killOwnScrollTriggers() {
    ownScrollTriggers.forEach(function(st) {
      st.kill();
    });
    ownScrollTriggers = [];
    log('Killed own ScrollTriggers');
  }

  function trackScrollTrigger(st) {
    ownScrollTriggers.push(st);
    return st;
  }

  // =============================================
  // SCROLL ANIMATIONS
  // =============================================

  function initScrollAnimations() {
    log('Initializing scroll animations');

    const sections = document.querySelectorAll(CONFIG.SELECTORS.section);
    log('Found ' + sections.length + ' section(s)');

    sections.forEach(function(section, index) {
      const sectionId = section.dataset.sectionId;
      log('Processing section ' + index + ' (ID: ' + sectionId + ')');
      initSectionAnimation(section, sectionId, index);
    });
  }

  function initSectionAnimation(section, sectionId, index) {
    const grid = section.querySelector(CONFIG.SELECTORS.grid);
    const stickyContent = section.querySelector(CONFIG.SELECTORS.stickyContent);
    const smallColumn = section.querySelector(CONFIG.SELECTORS.smallColumn);
    const smallColumnInner = section.querySelector(CONFIG.SELECTORS.smallColumnInner);
    const triggerButton = section.querySelector(CONFIG.SELECTORS.triggerButton);
    const largeButton = section.querySelector(CONFIG.SELECTORS.largeButton);

    if (!grid || !stickyContent) {
      log('Section ' + index + ': Missing required elements');
      return;
    }

    // Use smallColumnInner for height calculation (same as vanilla JS)
    const heightElement = smallColumnInner || smallColumn;
    if (!heightElement) {
      log('Section ' + index + ': No small column found');
      return;
    }

    // Calculate pin duration based on height difference
    const stickyHeight = stickyContent.offsetHeight;
    const smallHeight = heightElement.offsetHeight;
    const pinDistance = stickyHeight - smallHeight;

    log('Section ' + index + ' heights:', {
      stickyHeight: stickyHeight,
      smallHeight: smallHeight,
      pinDistance: pinDistance
    });

    if (pinDistance <= 0) {
      log('Section ' + index + ': No pin distance, skipping');
      return;
    }

    // ============================================
    // MAIN STICKY PIN ANIMATION
    // ============================================
    trackScrollTrigger(ScrollTrigger.create({
      trigger: grid,
      start: 'top 2px',
      end: '+=' + pinDistance,
      pin: stickyContent,
      scrub: true,
      markers: CONFIG.DEBUG,
      id: 'sticky-' + sectionId,
      onEnter: function() {
        log('Section ' + index + ': Pin ACTIVATED');
      },
      onLeave: function() {
        log('Section ' + index + ': Pin DEACTIVATED (scrolled past)');
      },
      onEnterBack: function() {
        log('Section ' + index + ': Pin ACTIVATED (scroll back)');
      },
      onLeaveBack: function() {
        log('Section ' + index + ': Pin DEACTIVATED (scroll back above)');
      }
    }));

    // ============================================
    // BUTTON FADE ANIMATION
    // ============================================
    const button = triggerButton || largeButton;

    if (button) {
      // Use a GSAP tween for better performance (batched RAF updates)
      var buttonTween = gsap.fromTo(button,
          { opacity: 1, pointerEvents: 'auto' },
          { opacity: 0, pointerEvents: 'none', paused: true }
      );

      trackScrollTrigger(ScrollTrigger.create({
        trigger: grid,
        start: 'top top',
        end: 'top -50px',
        scrub: true,
        id: 'button-fade-' + sectionId,
        animation: buttonTween
      }));

      log('Section ' + index + ': Button fade animation created');
    }

    log('Section ' + index + ': Animations initialized');
  }

  // =============================================
  // DRAWER CONTROLS
  // =============================================

  function initDrawerControls() {
    // Only bind listeners once to prevent duplicates
    if (drawerListenersBound) {
      log('Drawer controls already bound, skipping');
      return;
    }
    drawerListenersBound = true;
    log('Initializing drawer controls');

    // Use event delegation on document for all drawer interactions
    document.addEventListener('click', function(e) {
      var trigger, sectionId, drawerOne, overlay, drawerSub, subOverlay;

      // Drawer trigger buttons
      trigger = e.target.closest(CONFIG.SELECTORS.drawerTrigger);
      if (trigger) {
        sectionId = trigger.dataset.drawerTrigger;
        drawerOne = document.querySelector('[data-drawer-one="' + sectionId + '"]');
        overlay = document.querySelector('[data-drawer-overlay="' + sectionId + '"]');
        if (drawerOne && overlay) {
          drawerOne.scrollTop = 0;
          drawerOne.classList.add('is-open');
          overlay.classList.add('is-open');
          document.body.style.overflow = 'hidden';
          log('Drawer opened: ' + sectionId);
        }
        return;
      }

      // Drawer close buttons
      trigger = e.target.closest(CONFIG.SELECTORS.drawerClose);
      if (trigger) {
        sectionId = trigger.dataset.drawerClose;
        drawerOne = document.querySelector('[data-drawer-one="' + sectionId + '"]');
        overlay = document.querySelector('[data-drawer-overlay="' + sectionId + '"]');
        if (drawerOne && overlay) {
          drawerOne.classList.remove('is-open');
          overlay.classList.remove('is-open');
          document.body.style.overflow = '';
          log('Drawer closed: ' + sectionId);
        }
        return;
      }

      // Sub drawer trigger buttons
      trigger = e.target.closest(CONFIG.SELECTORS.subDrawerTrigger);
      if (trigger) {
        sectionId = trigger.dataset.subDrawerTrigger;
        drawerSub = document.querySelector('[data-drawer-sub="' + sectionId + '"]');
        if (drawerSub) {
          drawerSub.scrollTop = 0;
          drawerSub.classList.add('is-open');
          subOverlay = document.querySelector('[data-sub-drawer-overlay="' + sectionId + '"]');
          if (subOverlay) subOverlay.classList.add('is-open');
          log('Sub-drawer opened: ' + sectionId);
        }
        return;
      }

      // Sub drawer close buttons
      trigger = e.target.closest(CONFIG.SELECTORS.subDrawerClose);
      if (trigger) {
        sectionId = trigger.dataset.subDrawerClose;
        drawerSub = document.querySelector('[data-drawer-sub="' + sectionId + '"]');
        drawerOne = document.querySelector('[data-drawer-one="' + sectionId + '"]');
        overlay = document.querySelector('[data-drawer-overlay="' + sectionId + '"]');
        subOverlay = document.querySelector('[data-sub-drawer-overlay="' + sectionId + '"]');
        if (drawerSub && drawerOne && overlay) {
          drawerSub.classList.remove('is-open');
          if (subOverlay) subOverlay.classList.remove('is-open');
          setTimeout(function() {
            drawerOne.classList.remove('is-open');
            overlay.classList.remove('is-open');
            document.body.style.overflow = '';
            log('Sub-drawer closed, then drawer one: ' + sectionId);
          }, 400);
        }
        return;
      }

      // Overlay click to close
      trigger = e.target.closest(CONFIG.SELECTORS.drawerOverlay);
      if (trigger) {
        sectionId = trigger.dataset.drawerOverlay;
        drawerOne = document.querySelector('[data-drawer-one="' + sectionId + '"]');
        if (drawerOne) drawerOne.classList.remove('is-open');
        trigger.classList.remove('is-open');
        document.body.style.overflow = '';
        log('Drawer closed via overlay: ' + sectionId);
        return;
      }

      // Sub drawer overlay click to close
      trigger = e.target.closest(CONFIG.SELECTORS.subDrawerOverlay);
      if (trigger) {
        sectionId = trigger.dataset.subDrawerOverlay;
        drawerSub = document.querySelector('[data-drawer-sub="' + sectionId + '"]');
        drawerOne = document.querySelector('[data-drawer-one="' + sectionId + '"]');
        overlay = document.querySelector('[data-drawer-overlay="' + sectionId + '"]');
        if (drawerSub) drawerSub.classList.remove('is-open');
        trigger.classList.remove('is-open');
        setTimeout(function() {
          if (drawerOne) drawerOne.classList.remove('is-open');
          if (overlay) overlay.classList.remove('is-open');
          document.body.style.overflow = '';
        }, 400);
        log('Sub-drawer + drawer closed via sub-overlay: ' + sectionId);
        return;
      }
    });

    // Escape key to close drawers
    document.addEventListener('keydown', function(e) {
      if (e.key !== 'Escape') return;

      // Check for any open sub-drawer first
      var openSub = document.querySelector('.drawer-level-sub.is-open');
      if (openSub) {
        var sectionId = openSub.dataset.drawerSub;
        openSub.classList.remove('is-open');
        var subOverlay = document.querySelector('[data-sub-drawer-overlay="' + sectionId + '"]');
        if (subOverlay) subOverlay.classList.remove('is-open');

        var drawerOne = document.querySelector('[data-drawer-one="' + sectionId + '"]');
        var overlay = document.querySelector('[data-drawer-overlay="' + sectionId + '"]');
        setTimeout(function() {
          if (drawerOne) drawerOne.classList.remove('is-open');
          if (overlay) overlay.classList.remove('is-open');
          document.body.style.overflow = '';
        }, 400);
        log('Sub-drawer + drawer closed via Escape: ' + sectionId);
        return;
      }

      // Then check for any open level-one drawer
      var openOne = document.querySelector('.drawer-level-one.is-open');
      if (openOne) {
        var sectionId = openOne.dataset.drawerOne;
        var overlay = document.querySelector('[data-drawer-overlay="' + sectionId + '"]');
        openOne.classList.remove('is-open');
        if (overlay) overlay.classList.remove('is-open');
        document.body.style.overflow = '';
        log('Drawer closed via Escape: ' + sectionId);
      }
    });

    // Watch for sub-drawer open/close and control its videos
    var allSubDrawers = document.querySelectorAll(CONFIG.SELECTORS.drawerSub);
    allSubDrawers.forEach(function(drawerSub) {
      var subDrawerObserver = new MutationObserver(function(mutations) {
        mutations.forEach(function(mutation) {
          if (mutation.attributeName !== 'class') return;

          if (drawerSub.classList.contains('is-open')) {
            // Drawer opened — play only the active video (desktop or mobile based on viewport)
            var videoType = window.innerWidth >= 744 ? 'desktop' : 'mobile';
            var activeVideo = drawerSub.querySelector('video[data-video-type="' + videoType + '"]');
            if (activeVideo) {
              activeVideo.play().catch(function() {});
            }
            var controlBtns = drawerSub.querySelectorAll('.video-control-btn');
            controlBtns.forEach(function(btn) {
              btn.classList.add('is-playing');
            });
            log('Sub-drawer video playing (' + videoType + ')');
          } else {
            // Drawer closed — pause and reset all videos
            var videos = drawerSub.querySelectorAll('video');
            videos.forEach(function(video) {
              video.pause();
              video.currentTime = 0;
            });
            var controlBtns = drawerSub.querySelectorAll('.video-control-btn');
            controlBtns.forEach(function(btn) {
              btn.classList.remove('is-playing');
            });
            log('Sub-drawer videos reset');
          }
        });
      });
      subDrawerObserver.observe(drawerSub, { attributes: true, attributeFilter: ['class'] });
    });

    log('Drawer controls initialized');
  }

  // =============================================
  // INITIALIZATION
  // =============================================

  function init() {
    log('=== HomeBlock03 GSAP Script Loaded ===');
    log('Breakpoint: ' + CONFIG.BREAKPOINT + 'px');
    log('Current width: ' + window.innerWidth + 'px');
    log('GSAP version: ' + (typeof gsap !== 'undefined' ? gsap.version : 'NOT LOADED'));
    log('ScrollTrigger available: ' + (typeof ScrollTrigger !== 'undefined'));

    // Check if GSAP and ScrollTrigger are loaded
    if (typeof gsap === 'undefined') {
      console.error('[HomeBlock03-GSAP] GSAP is not loaded! Make sure GSAP script is included before this file.');
      return;
    }

    if (typeof ScrollTrigger === 'undefined') {
      console.error('[HomeBlock03-GSAP] ScrollTrigger is not loaded! Make sure ScrollTrigger script is included.');
      return;
    }

    // Register ScrollTrigger plugin
    gsap.registerPlugin(ScrollTrigger);
    log('ScrollTrigger plugin registered');

    // Use matchMedia for responsive handling — automatically cleans up
    // ScrollTriggers when viewport drops below breakpoint, and re-creates
    // them when it crosses back above. No manual resize listener needed.
    ScrollTrigger.matchMedia({
      ['(min-width: ' + CONFIG.BREAKPOINT + 'px)']: function() {
        log('matchMedia: desktop breakpoint active');
        initScrollAnimations();

        // Return cleanup function — called when media query no longer matches
        return function() {
          log('matchMedia: desktop breakpoint inactive, cleaning up');
          killOwnScrollTriggers();
        };
      }
    });

    // Drawer controls work at all breakpoints
    initDrawerControls();

    log('Initialization complete');
  }

  // =============================================
  // INITIALIZATION TRACKING
  // =============================================

  let isInitialized = false;

  function ensureInitialized() {
    if (!isInitialized) {
      init();
      isInitialized = true;
    }
  }

  // =============================================
  // EXECUTE ON DOM READY
  // =============================================

  if (document.readyState === 'loading') {
    log('DOM loading, waiting for DOMContentLoaded...');
    document.addEventListener('DOMContentLoaded', ensureInitialized);
  } else {
    log('DOM already loaded, initializing immediately...');
    ensureInitialized();
  }

  // =============================================
  // SHOPIFY THEME EDITOR SUPPORT
  // =============================================

  if (typeof Shopify !== 'undefined' && Shopify.designMode) {
    log('Shopify design mode detected');

    document.addEventListener('shopify:section:load', function(event) {
      log('Shopify section loaded, reinitializing...', event.detail);
      ensureInitialized();
      setTimeout(function() {
        killOwnScrollTriggers();
        initScrollAnimations();
      }, 100);
    });

    document.addEventListener('shopify:section:unload', function(event) {
      log('Shopify section unloaded, cleaning up...', event.detail);
      killOwnScrollTriggers();
    });

    document.addEventListener('shopify:section:select', function(event) {
      log('Shopify section selected', event.detail);
      ScrollTrigger.refresh();
    });
  }

  // =============================================
  // PUBLIC API (for re-initialization if needed)
  // =============================================

  return {
    refresh: function() {
      ScrollTrigger.refresh();
    },
    reinit: function() {
      killOwnScrollTriggers();
      initScrollAnimations();
    }
  };

})();
